/** Fighter level and the skill point bar (docs/game-design.md §3–4). */
import { PROGRESSION } from '../config/progression';

/** XP needed to go from `level` to `level + 1`. */
export function xpToNextLevel(level: number): number {
  return PROGRESSION.level.base + PROGRESSION.level.perLevel * level;
}

export interface LevelInfo {
  level: number;
  /** XP gathered inside the current level. */
  into: number;
  /** XP needed for the next level (0 at max level). */
  needed: number;
  maxed: boolean;
}

export function levelFromXp(totalXp: number): LevelInfo {
  let level = 1;
  let rest = Math.max(0, Math.floor(totalXp));
  while (level < PROGRESSION.level.max) {
    const need = xpToNextLevel(level);
    if (rest < need) return { level, into: rest, needed: need, maxed: false };
    rest -= need;
    level++;
  }
  return { level, into: 0, needed: 0, maxed: true };
}

/** XP for the next skill point, given points earned from XP so far. */
export function nextSkillPointCost(pointsEarned: number): number {
  const n = Math.min(pointsEarned, PROGRESSION.skillPoints.maxFromXp);
  return PROGRESSION.skillPoints.base + PROGRESSION.skillPoints.perPoint * n;
}

export interface SkillBar {
  pointsEarned: number;
  barXp: number;
}

export interface SkillBarGain extends SkillBar {
  gained: number;
  overflowCoins: number;
}

/** Adds XP to the skill point bar. After 150 points each fill pays coins instead. */
export function addSkillXp(bar: SkillBar, xp: number): SkillBarGain {
  let { pointsEarned, barXp } = bar;
  let gained = 0;
  let overflowCoins = 0;
  barXp += Math.max(0, Math.floor(xp));
  for (;;) {
    const cost = nextSkillPointCost(pointsEarned);
    if (barXp < cost) break;
    barXp -= cost;
    if (pointsEarned < PROGRESSION.skillPoints.maxFromXp) {
      pointsEarned++;
      gained++;
    } else {
      overflowCoins += PROGRESSION.skillPoints.overflowCoins;
    }
  }
  return { pointsEarned, barXp, gained, overflowCoins };
}
